import React, { useCallback, useEffect, useState } from 'react';
import { getIntakeAddresses, setIntakeAddresses } from './api';

/**
 * The business's own invoice addresses (invoices@yourbusiness.co.uk). Email sent to them, or read from them, is this
 * business's: suppliers use the address as it is, no "+tag" (backend/email_routing.py). Owners and admins edit it.
 */
export default function IntakeAddresses() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getIntakeAddresses();
      if (!d) return;
      setData(d);
      setDraft((d.addresses || []).join(', '));
    } catch (e) {
      if (e.status !== 403 && e.status !== 404) setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  if (!data && !error) return null;

  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      const list = draft.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      const d = await setIntakeAddresses(list);
      setData((old) => ({ ...old, ...d }));
      setDraft((d.addresses || []).join(', '));
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addresses = data?.addresses || [];
  return (
    <div className="space-y-1" data-testid="intake-addresses">
      <p className="text-ink">
        Invoice addresses{' '}
        <span className="text-ink-muted">
          · suppliers send invoices to these as they are; no special tag needed.
        </span>
      </p>
      {data?.can_edit ? (
        <div className="flex flex-wrap items-center gap-2">
          <input aria-label="Invoice addresses" value={draft} onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
            placeholder="invoices@yourbusiness.co.uk" data-testid="intake-addresses-input"
            className="flex-1 min-w-[240px] border rounded px-2 py-1" />
          <button type="button" disabled={busy} onClick={save} data-testid="intake-addresses-save"
            className="px-3 py-1 rounded bg-primary-600 text-white disabled:opacity-50">Save</button>
          {saved && <span className="text-emerald-700" data-testid="intake-addresses-saved">Saved</span>}
        </div>
      ) : (
        <p className="text-ink-muted" data-testid="intake-addresses-list">
          {addresses.length ? addresses.join(', ') : 'None set yet. An owner or admin can add them.'}
        </p>
      )}
      {error && <p className="text-red-700" data-testid="intake-addresses-error">{error}</p>}
    </div>
  );
}
